<template>
  <span class="x-distance">
    <slot :value="convertedValue" :label="label">
      {{ convertedValue }}
      <span v-if="labelType !== 'none'" class="x-distance-unit">
        {{ label }}
      </span>
    </slot>
  </span>
</template>

<!-- eslint-disable vue/block-lang -->
<script>
/* eslint-disable vue/component-api-style */
// Conversion functions
// Convert from Feet to Miles
export const feetToMiles = (feet) => feet / 5280
// Convert from Feet to Meters
export const feetToMeters = (feet) => feet / 3.2808
// Convert from Feet to Yards
export const feetToYards = (feet) => feet * 0.33333
// Convert from Feet to Kilometers
export const feetToKilometers = (feet) => feet / 3280.8
// Convert from Meters to Feet
export const metersToFeet = (meters) => meters * 3.2808
// Convert from Meters to Yards
export const metersToYards = (meters) => meters * 1.09361367
// Convert from Meters to Kilometers
export const metersToKilometers = (meters) => meters / 1000
// Convert from Meters to Miles
export const metersToMiles = (meters) => meters * 0.000621371192
// Convert from Yards to Meters
export const yardsToMeters = (yards) => yards / 1.09361367
// Convert from Yards to Feet
export const yardsToFeet = (yards) => yards * 3
// Convert from Yards to Kilometers
export const yardsToKilometers = (yards) => yards / 1094
// Convert from Yards to Miles
export const yardsToMiles = (yards) => yards / 1760
// Convert from Kilometers to Feet
export const kilometersToFeet = (kilometers) => kilometers * 3280.8
// Convert from Kilometers to Yards
export const kilometersToYards = (kilometers) => kilometers * 1094
// Convert from Kilometers to Meters
export const kilometersToMeters = (kilometers) => kilometers * 1000
// Convert from Kilometers to Miles
export const kilometersToMiles = (kilometers) => kilometers * 0.62137
// Convert from Miles to Feet
export const milesToFeet = (miles) => miles * 5280
// Convert from Miles to Meters
export const milesToMeters = (miles) => miles * 1609.344
// Convert from Miles to Kilometers
export const milesToKilometers = (miles) => miles * 1.609344
// Convert from Miles to Yards
export const milesToYards = (miles) => miles * 1760
// Convert from meters to inches
export const metersToInches = (meters) => meters * 39.37007874
// Convert from meters to centimeters
export const metersToCentimeters = (meters) => meters * 100
// Convert from feet to inches
export const feetToInches = (feet) => feet * 12
// Convert from yards to inches
export const yardsToInches = (yards) => yards * 36
// Convert from kilometers to inches
export const kilometersToInches = (kilometers) => kilometers * 39370.1
// Convert miles to inches
export const milesToInches = (miles) => miles * 63360
// Convert feet to centimeters
export const feetToCentimeters = (feet) => feet * 30.48
// Convert yards to centimeters
export const yardsToCentimeters = (yards) => yards * 91.44
// Convert kilometers to centimeters
export const kilometersToCentimeters = (kilometers) => kilometers * 100000
// Convert miles to centimeters
export const milesToCentimeters = (miles) => miles * 160934

/**
 * Convert a given value to meters
 *
 * @param units - units the value is currently in
 * @param value - value to be converted to meters
 * @returns value in meters
 */
export function convertToMeters(units, value) {
  if (units !== 'meters') {
    const converter = getConversionFunction(units, 'meters')
    return converter('meters')(Number(value))
  }
  return value
}

// Utils
// Capitalize string
const capitalize = (value) =>
  `${value.charAt(0).toUpperCase()}${value.slice(1)}`
// Get a conversion function if available
export const getConversionFunction = (from) => (to) => {
  // All supported conversions
  const conversionFunctions = {
    feetToMeters,
    feetToYards,
    feetToMiles,
    feetToKilometers,
    metersToFeet,
    metersToYards,
    metersToKilometers,
    metersToMiles,
    yardsToMeters,
    yardsToFeet,
    yardsToKilometers,
    yardsToMiles,
    kilometersToFeet,
    kilometersToMeters,
    kilometersToMiles,
    kilometersToYards,
    milesToFeet,
    milesToYards,
    milesToMeters,
    milesToKilometers,
    metersToInches,
    metersToCentimeters,
    feetToInches,
    yardsToInches,
    kilometersToInches,
    milesToInches,
    feetToCentimeters,
    yardsToCentimeters,
    kilometersToCentimeters,
    milesToCentimeters,
  }
  if (from === to) {
    return (value) => value
  }
  const convert = conversionFunctions?.[`${from}To${capitalize(to)}`]
  if (convert) {
    return convert
  }
  throw new Error(`Conversion from ${from} to ${to} not supported`)
}
export const isImperial = (unit) =>
  ['in', 'inches', 'feet', 'ft', 'miles', 'mi', 'yards', 'yds'].indexOf(
    unit
  ) !== -1
export const isMetric = (unit) =>
  ['cm', 'centimeters', 'meters', 'm', 'kilometers', 'km'].indexOf(unit) !== -1
export const getUnitType = (unit) => (isImperial(unit) ? 'imperial' : 'metric')

export const getUnitAbbreviation = (unit) =>
  Object.freeze({
    feet: 'ft',
    meters: 'm',
    yards: 'yds',
    kilometers: 'km',
    miles: 'mi',
    centimeters: 'cm',
    inches: 'in',
  })[unit]
export const getUnitFullNameFromAbbreviation = (unit) =>
  Object.freeze({
    ft: 'feet',
    m: 'meters',
    yds: 'yards',
    km: 'kilometers',
    mi: 'miles',
    in: 'inches',
    cm: 'centimeters',
  })[unit]

// Defaults
export const DEFAULT_UNIT_TYPE = 'imperial'
export const imperialToMetricDefaults = Object.freeze({
  feet: 'meters',
  miles: 'kilometers',
  yards: 'meters',
})
export const metricToImperialDefaults = Object.freeze({
  meters: 'feet',
  kilometers: 'miles',
})

/* eslint-disable jsdoc/no-types -- @type annotation required: this is a JS file where `as const`
   is unavailable, so the annotation is the only way to preserve a narrow string-literal tuple.
   Without it, `(typeof supportedFromTypes)[number]` collapses to `string` in math.ts. */
/** @type {readonly ['meters', 'm', 'kilometers', 'km', 'feet', 'ft', 'miles', 'mi', 'yards', 'yds']} */
/* eslint-enable jsdoc/no-types */
export const supportedFromTypes = Object.freeze([
  'meters',
  'm',
  'kilometers',
  'km',
  'feet',
  'ft',
  'miles',
  'mi',
  'yards',
  'yds',
])

export const supportedToTypes = Object.freeze([
  'auto',
  'meters',
  'm',
  'kilometers',
  'km',
  'feet',
  'ft',
  'miles',
  'mi',
  'yards',
  'yds',
  'cm',
  'centimeters',
  'in',
  'inches',
])

/* eslint-disable jsdoc/no-types -- @type annotation required: this is a JS file where `as const`
   is unavailable, so the annotation is the only way to preserve a narrow string-literal tuple.
   Without it, `(typeof supportedLabelTypes)[number]` collapses to `string` in math.ts. */
/** @type {readonly ['none', 'abbreviation', 'full']} */
/* eslint-enable jsdoc/no-types */
export const supportedLabelTypes = Object.freeze([
  'none',
  'abbreviation',
  'full',
])

// Number formatter to add commas and fractions
export const formatNumber = (maximumFractionDigits) => (num) =>
  num.toLocaleString(undefined, {
    maximumFractionDigits,
  })

/**
 * Utility component for converting distances between different units.

 */
export default {
  name: 'XDistance',
  props: {
    /**
     * Value to convert
     */
    length: {
      type: [Number, String],
      default: undefined,
    },
    /**
     * Unit system to convert from
     */
    from: {
      type: String,
      default: 'meters',
      validator: function (value) {
        return supportedFromTypes.indexOf(value) !== -1
      },
    },
    /**
     * Unit system to convert to.
     * Automatic conversions when using "auto".
     */
    to: {
      type: String,
      default: 'auto',
      validator: function (value) {
        return supportedToTypes.indexOf(value) !== -1
      },
    },
    /**
     * Maximum number of fraction digits to display
     * Large units of measurement (km, mi) will show one digit when set to 'auto'.
     */
    maximumFractionDigits: {
      type: [Number, String],
      default: 'auto',
    },
    /**
     * Label type of unit displayed
     */
    labelType: {
      type: String,
      default: 'abbreviation',
      validator: function (value) {
        return supportedLabelTypes.indexOf(value) !== -1
      },
    },
  },
  computed: {
    getUserSettingUnitSystem() {
      return this.$storage?.getItem('unitType') ?? DEFAULT_UNIT_TYPE
    },
    convertedValue() {
      // If the length is undefined (not 0) display '--'
      if (
        this.length === null ||
        this.length === undefined ||
        this.length === ''
      ) {
        return '--'
      }
      // Convert this.length to a number, ensuring to remove any commas (thousands separators)
      // in case it is a formatted value.
      const value = Number(this.length.toString().replace(/,/g, ''))
      // Convert abbreviations to full names for comparisons
      const fullTo = getUnitFullNameFromAbbreviation(this.to) ?? this.to
      const fullFrom = getUnitFullNameFromAbbreviation(this.from) ?? this.from
      let maxNumDigits = 0
      if (this.maximumFractionDigits === 'auto') {
        if (fullFrom === 'kilometers' || fullFrom === 'miles') {
          maxNumDigits = 1
        }
      } else {
        maxNumDigits = this.maximumFractionDigits
      }
      const format = formatNumber(maxNumDigits)
      const convertTo = getConversionFunction(fullFrom)
      // Check if conversion is needed
      if (fullTo === fullFrom) {
        return format(value)
      }
      // Check if we should use the user unit settings
      // to do default conversions
      if (fullTo === 'auto') {
        if (this.getUserSettingUnitSystem === 'imperial') {
          // No conversion necesary
          if (isImperial(fullFrom)) {
            return format(value)
          }
          // Convert to default unit type
          return format(convertTo(metricToImperialDefaults[fullFrom])(value))
        }
        if (this.getUserSettingUnitSystem === 'metric') {
          // No conversion necesary
          if (isMetric(fullFrom)) {
            return format(value)
          }
          // Convert to default unit type
          return format(convertTo(imperialToMetricDefaults[fullFrom])(value))
        }
      }
      // Do conversion based on props
      return format(convertTo(fullTo)(value))
    },
    label() {
      const getLabel = (to) =>
        ({
          abbreviation: getUnitAbbreviation,
          full: (full) => full,
          none: () => '',
        })[this.labelType](to)
      // Convert abbreviations to full names for comparisons
      const fullTo = getUnitFullNameFromAbbreviation(this.to) ?? this.to
      const fullFrom = getUnitFullNameFromAbbreviation(this.from) ?? this.from

      if (fullTo === fullFrom) {
        return getLabel(fullFrom)
      }
      if (fullTo === 'auto') {
        if (this.getUserSettingUnitSystem === 'imperial') {
          if (isImperial(fullFrom)) {
            return getLabel(fullFrom)
          }
          return getLabel(metricToImperialDefaults[fullFrom])
        }
        if (this.getUserSettingUnitSystem === 'metric') {
          if (isMetric(fullFrom)) {
            return getLabel(fullFrom)
          }
          return getLabel(imperialToMetricDefaults[fullFrom])
        }
      }
      return getLabel(fullTo)
    },
  },
}
</script>
